import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Autocomplete,
  CircularProgress,
  Paper,
  InputAdornment,
  Collapse,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  PriorityHigh as PriorityHighIcon,
  LocalPhone as PhoneIcon,
  Email as EmailIcon,
  NoteAdd as NoteAddIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import { Employee, EnquiryFormData, Party, Product, StatusNoteCreate } from '../../types';

// Add this interface for status note


const CreateEditEnquiry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    getValues,
  } = useForm<EnquiryFormData>({
    defaultValues: {
      enquiry_no: '',
      enquiry_date: new Date(),
      status: 'NEW',
      priority: 'MEDIUM',
      source: 'CALL',
      subject: '',
      description: '',
    }
  });

  // Watch status for conditional fields
  const status = watch('status');
  const partyId = watch('party_id');
  const assignedEmployeeId = watch('assigned_employee_id');
  const previousStatus = watch('previous_status');

  // State
  const [parties, setParties] = useState<Party[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setError] = useState('');
  const [, setSuccess] = useState('');
  const [statusNotes, setStatusNotes] = useState<StatusNoteCreate[]>([]);
  const [showStatusNote, setShowStatusNote] = useState(false);
  const [, setStatusHistory] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch parties
      const partiesResponse = await request({
        url: 'party/get-all-parties',
        method: 'get',
      });
      setParties(partiesResponse.parties || []);

      // Fetch employees
      const employeesResponse = await request({
        url: 'employee/get-all-employees',
        method: 'get',
      });
      setEmployees(employeesResponse.data || []);

      // Fetch products
      const productsResponse = await request({
        url: 'product/get-all-products',
        method: 'get',
      });
      setProducts(productsResponse.products || []);

      if (isEditMode && id) {
        // Fetch single enquiry data for edit
        const enquiryResponse = await request({
          url: `/enquiry/get-single-enquiry/${id}`,
          method: 'get',
        });

        const enquiryData = enquiryResponse.data || enquiryResponse;

        if (!enquiryData) {
          throw new Error('Enquiry data not found');
        }

        // Fetch status history if exists
        if (enquiryData.status_history) {
          setStatusHistory(enquiryData.status_history);
        }

        if (enquiryData.status_notes) {
          setStatusNotes(enquiryData.status_notes);
        }

        // Format dates for react-hook-form
        const formattedData: EnquiryFormData = {
          enquiry_no: enquiryData.enquiry_no || '',
          enquiry_date: enquiryData.enquiry_date ? new Date(enquiryData.enquiry_date) : new Date(),
          party_id: enquiryData.party_id || undefined,
          subject: enquiryData.subject || '',
          description: enquiryData.description || '',
          assigned_employee_id: enquiryData.assigned_employee_id || undefined,
          assigned_date: enquiryData.assigned_date ? new Date(enquiryData.assigned_date) : undefined,
          status: enquiryData.status || 'NEW',
          status_note: enquiryData.status_note || '', // Add status_note field
          status_changed_by: enquiryData.status_changed_by || undefined,
          status_changed_at: enquiryData.status_changed_at ? new Date(enquiryData.status_changed_at) : undefined,
          closed_result: enquiryData.closed_result || undefined,
          cancelled_reason: enquiryData.cancelled_reason || undefined,
          priority: enquiryData.priority || 'MEDIUM',
          source: enquiryData.source || 'CALL',
          product_id: enquiryData.product_id || [],
        };

        reset(formattedData);
        
        // Store the original status to track changes
        setValue('previous_status', enquiryData.status);
      } else {
        // Generate enquiry number for new enquiry
        const enquiryNo = await generateEnquiryNumber();
        setValue('enquiry_no', enquiryNo);
        setValue('enquiry_date', new Date());
        setValue('previous_status', 'NEW'); // For new enquiries
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset, setValue]);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  // Show status note field when status changes
  useEffect(() => {
    if (isEditMode && previousStatus && status !== previousStatus) {
      setShowStatusNote(true);
    } else {
      setShowStatusNote(false);
    }
  }, [status, previousStatus, isEditMode]);

  const generateEnquiryNumber = async () => {
    try {
      // Fetch last enquiry number from API
      const response = await request({
        url: '/enquiry/last-enquiry-no',
        method: 'get',
      });
      const lastNo = response.data?.lastNo || 'ENQ-000000';
      const nextNumber = parseInt(lastNo.split('-')[1]) + 1;
      return `ENQ-${nextNumber.toString().padStart(6, '0')}`;
    } catch (err) {
      // Fallback generation
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `ENQ-${year}${month}${random}`;
    }
  };

  const onSubmit = async (data: EnquiryFormData) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload: any = {
        enquiry_date: format(new Date(data.enquiry_date), 'yyyy-MM-dd'),
        party_id: data.party_id || null,
        subject: data.subject,
        description: data.description,
        assigned_employee_id: data.assigned_employee_id || null,
        assigned_date: data.assigned_date ? format(new Date(data.assigned_date), 'yyyy-MM-dd') : null,
        status: data.status,
        status_note: data.status_note || '', // Include status note
        priority: data.priority,
        source: data.source,
        product_id: data.product_id || [],
      };

      // Only include closed_result if status is CLOSED
      if (data.status === 'CLOSED' && data.closed_result) {
        payload.closed_result = data.closed_result;
      }

      // Only include cancelled_reason if status is CANCELLED
      if (data.status === 'CANCELLED' && data.cancelled_reason) {
        payload.cancelled_reason = data.cancelled_reason;
      }

      // For new enquiries, include the status note as initial note
      if (!isEditMode && data.status_note) {
        payload.status_note = data.status_note;
      }

      // Remove null/empty values for cleaner payload
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === '' ||
          (Array.isArray(payload[key]) && payload[key].length === 0)) {
          delete payload[key];
        }
      });

      const apiUrl = isEditMode ? `enquiry/update-enquiry/${id}` : '/enquiry/create-enquiry';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload,
      });

      if (res.success) {
        toast.success(res.message || `Enquiry ${isEditMode ? 'updated' : 'created'} successfully!`);
        navigate('/enquiry');
      }

    } catch (err: any) {
      toast.error(err)
    } finally {
      setSaving(false);
    }
  };

  // Add status note separately
  const handleAddStatusNote = async () => {
    const currentStatus = getValues('status');
    const statusNote = getValues('status_note');

    if (!statusNote) {
      toast.error('Please enter a note before adding');
      return;
    }

    try {
      const res = await request({
        url: `/enquiry/add-status-note/${id}`,
        method: 'post',
        data: { note: statusNote },
      });

      if (res.success) {
        toast.success('Status note added successfully!');
        setStatusNotes(prev => [...prev, {
          previous_status: previousStatus,
          current_status: currentStatus,
          note: statusNote,
          created_at: new Date()
        }]);
        setValue('status_note', '');
        setShowStatusNote(false);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to add status note');
    }
  };

  // Helper functions
  const getSelectedParty = () => {
    return parties.find(party => party._id === partyId);
  };

  const getSelectedEmployee = () => {
    return employees.find(employee => employee._id === assignedEmployeeId);
  };

  const getSelectedProducts = () => {
    const productIds = watch('product_id') || [];
    return products.filter(product => productIds.includes(product._id));
  };

  const selectedParty = getSelectedParty();
  const selectedEmployee = getSelectedEmployee();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
          Loading enquiry data...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            p: 2,
            backgroundColor: 'white',
            color: 'white',
            borderRadius: 1,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {isEditMode ? 'Edit Enquiry' : 'Create New Enquiry'}
                </Typography>
                <Typography variant="body2" color='secondary.contrastText' sx={{ opacity: 0.9 }}>
                  {isEditMode ? 'Update enquiry details' : 'Fill in the details to create a new enquiry'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Card elevation={2} sx={{ borderRadius: 1 }}>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Two Column Layout without Grid */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>

                  {/* LEFT COLUMN - Basic Information */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main'
                    }}>
                      <DescriptionIcon />
                      Basic Information
                    </Typography>

                    {/* Enquiry Date and Status in one row */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="enquiry_date"
                          control={control}
                          rules={{ required: 'Enquiry date is required' }}
                          render={({ field }) => (
                            <DatePicker
                              label="Enquiry Date *"
                              value={field.value ? new Date(field.value) : null}
                              onChange={field.onChange}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  error: !!errors.enquiry_date,
                                  helperText: errors.enquiry_date?.message,
                                }
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth size="small" error={!!errors.status}>
                              <InputLabel>Status *</InputLabel>
                              <Select
                                {...field}
                                label="Status *"
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                }}
                              >
                                <MenuItem value="NEW">🟢 New</MenuItem>
                                <MenuItem value="ASSIGNED">🔵 Assigned</MenuItem>
                                <MenuItem value="IN_PROGRESS">🟡 In Progress</MenuItem>
                                <MenuItem value="FOLLOW_UP">🟠 Follow Up</MenuItem>
                                <MenuItem value="CONVERTED">🟣 Converted</MenuItem>
                                <MenuItem value="CLOSED">⚫ Closed</MenuItem>
                                <MenuItem value="CANCELLED">🔴 Cancelled</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                    </Box>

                    {/* Priority and Source in one row */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Controller
                        name="priority"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select
                              {...field}
                              label="Priority"
                            >
                              <MenuItem value="LOW">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PriorityHighIcon color="success" />
                                  Low Priority
                                </Box>
                              </MenuItem>
                              <MenuItem value="MEDIUM">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PriorityHighIcon color="warning" />
                                  Medium Priority
                                </Box>
                              </MenuItem>
                              <MenuItem value="HIGH">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PriorityHighIcon color="error" />
                                  High Priority
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />

                      <Controller
                        name="source"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Source</InputLabel>
                            <Select {...field} label="Source">
                              <MenuItem value="CALL">📞 Call</MenuItem>
                              <MenuItem value="WHATSAPP">💬 WhatsApp</MenuItem>
                              <MenuItem value="EMAIL">✉️ Email</MenuItem>
                              <MenuItem value="WEBSITE">🌐 Website</MenuItem>
                              <MenuItem value="WALK_IN">🚶 Walk-in</MenuItem>
                              <MenuItem value="REFERRAL">👥 Referral</MenuItem>
                              <MenuItem value="SOCIAL_MEDIA">📱 Social Media</MenuItem>
                              <MenuItem value="OTHER">🔗 Other</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>

                    {/* Subject */}
                    <Controller
                      name="subject"
                      control={control}
                      rules={{ required: 'Subject is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Subject *"
                          error={!!errors.subject}
                          helperText={errors.subject?.message}
                          size="small"
                          sx={{ mb: 2 }}
                        />
                      )}
                    />

                    {/* Description */}
                    <Controller
                      name="description"
                      control={control}
                      rules={{ required: 'Description is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Description *"
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          multiline
                          rows={4}
                          size="small"
                          sx={{ mb: 2 }}
                        />
                      )}
                    />

                    {/* Status Note Field (Visible when status changes in edit mode or for new enquiries) */}
                    <Collapse in={showStatusNote || !isEditMode}>
                      <Box sx={{ mb: 3 }}>
                        <Controller
                          name="status_note"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Status Note"
                              placeholder="Add a note for this status change..."
                              multiline
                              rows={2}
                              size="small"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <NoteAddIcon color="action" />
                                  </InputAdornment>
                                ),
                              }}
                              helperText={isEditMode ? "Add a note explaining why the status was changed" : "Add a note for the initial status"}
                            />
                          )}
                        />
                        {isEditMode && showStatusNote && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<NoteAddIcon />}
                            onClick={handleAddStatusNote}
                            sx={{ mt: 1 }}
                          >
                            Add Note Separately
                          </Button>
                        )}
                      </Box>
                    </Collapse>

                    {/* Status-specific fields */}
                    {status === 'CLOSED' && (
                      <Controller
                        name="closed_result"
                        control={control}
                        rules={status === 'CLOSED' ? { required: 'Closed result is required' } : {}}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Closed Result *"
                            error={!!errors.closed_result}
                            helperText={errors.closed_result?.message || "Why was this enquiry closed?"}
                            multiline
                            rows={2}
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    )}

                    {status === 'CANCELLED' && (
                      <Controller
                        name="cancelled_reason"
                        control={control}
                        rules={status === 'CANCELLED' ? { required: 'Cancellation reason is required' } : {}}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Cancellation Reason *"
                            error={!!errors.cancelled_reason}
                            helperText={errors.cancelled_reason?.message || "Why was this enquiry cancelled?"}
                            multiline
                            rows={2}
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    )}

                    {/* Show existing status notes */}
                    {isEditMode && statusNotes.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HistoryIcon fontSize="small" />
                          Previous Status Notes
                        </Typography>
                        <Stack spacing={1}>
                          {statusNotes.map((note, index) => (
                            <Paper key={index} sx={{ p: 1.5, bgcolor: 'background.default' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {note.previous_status ? `${note.previous_status} → ${note.current_status}` : note.current_status}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {note.note}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                                </Typography>
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>

                  {/* RIGHT COLUMN - Party, Products, Employees */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main'
                    }}>
                      <BusinessIcon />
                      Additional Information
                    </Typography>

                    {/* Party Selection */}
                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="party_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <Autocomplete
                              size='small'
                              options={parties}
                              getOptionLabel={(option) => option.partyName}
                              value={selectedParty || null}
                              onChange={(_, newValue) => {
                                field.onChange(newValue?._id || '');
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Party"
                                  placeholder="Search party by name..."
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <>
                                        <InputAdornment position="start">
                                          <BusinessIcon color="action" />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2">{option.partyName}</Typography>
                                    {option.nickName && (
                                      <Typography variant="caption" color="text.secondary">
                                        ({option.nickName})
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      {option.entityCategory === 'WALK_IN_CUSTOMER' ? 'Walk-in Customer' :
                                        option.entityCategory === 'REGULAR_CUSTOMER' ? 'Regular Customer' : 'Party'}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                            />
                            <FormHelperText>
                              Select a party or leave empty for walk-in customers
                            </FormHelperText>
                          </FormControl>
                        )}
                      />

                      {/* Party Contact Info */}
                      {selectedParty && (
                        <Paper elevation={0} sx={{
                          p: 2,
                          mt: 1,
                          backgroundColor: 'background.default',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                            Party Contact Information
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {selectedParty.contact.phone?.map((phone, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {phone.phoneNo} {phone.label && `(${phone.label})`}
                                </Typography>
                              </Box>
                            ))}
                            {selectedParty.contact.email?.map((email, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">{email}</Typography>
                              </Box>
                            ))}
                            {selectedParty.location && (
                              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                📍 {selectedParty.location}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      )}
                    </Box>

                    {/* Employee Assignment */}
                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="assigned_employee_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <Autocomplete
                              size='small'
                              options={employees}
                              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                              value={selectedEmployee || null}
                              onChange={(_, newValue) => {
                                field.onChange(newValue?._id || '');
                                // Auto-set assigned date if not set
                                if (newValue && !watch('assigned_date')) {
                                  setValue('assigned_date', new Date());
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Assign to Employee"
                                  placeholder="Search employee..."
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <>
                                        <InputAdornment position="start">
                                          <PersonIcon color="action" />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2">
                                      {option.first_name} {option.last_name}
                                    </Typography>
                                    {option.contact.email?.[0] && (
                                      <Typography variant="caption" color="text.secondary">
                                        {option.contact.email[0]}
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              )}
                            />
                            <FormHelperText>
                              Assign this enquiry to a team member
                            </FormHelperText>
                          </FormControl>
                        )}
                      />

                      {/* Assigned Date */}
                      {assignedEmployeeId && (
                        <Controller
                          name="assigned_date"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label="Assigned Date"
                              type="date"
                              value={field.value ? format(new Date(field.value), 'yyyy-MM-dd') : ''}
                              onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : undefined)}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={{ mt: 2 }}
                              helperText="Date when enquiry was assigned to the employee"
                            />
                          )}
                        />
                      )}
                    </Box>

                    {/* Products Selection */}
                    <Box>
                      <Controller
                        name="product_id"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            size='small'
                            multiple
                            options={products}
                            getOptionLabel={(option) => `${option.name} (${option.partNo})`}
                            value={getSelectedProducts()}
                            onChange={(_, newValue) => {
                              const productIds = newValue.map(product => product._id);
                              field.onChange(productIds);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Products"
                                placeholder="Search products..."
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Part No: {option.partNo}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Brand: {option.brand}
                                    </Typography>
                                  </Box>
                                </Box>
                              </li>
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  label={`${option.name} (${option.partNo})`}
                                  size="small"
                                  variant="outlined"
                                  {...getTagProps({ index })}
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              ))
                            }
                          />
                        )}
                      />
                      <FormHelperText>
                        Select products related to this enquiry (optional)
                      </FormHelperText>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Action Buttons */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}>
                  <Button
                    variant="outlined"
                    size='small'
                    color="inherit"
                    onClick={() => navigate(-1)}
                    disabled={saving}
                    sx={{ minWidth: 120, px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size='small'
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={saving}
                    sx={{ minWidth: 140, px: 4 }}
                  >
                    {saving ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      isEditMode ? 'Update Enquiry' : 'Create Enquiry'
                    )}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>
      </LocalizationProvider>
    </>
  );
};

export default CreateEditEnquiry;